/**
 * Article card — a single PubMed-derived article rendered as a row inside
 * a day group. Bilingual title and abstract; click-through navigates to
 * the article detail route (`#/article/<pmid>`).
 *
 * The abstract is collapsed to 3 lines by default with a "展开 / 收起"
 * toggle. Translation may be missing (`title_zh` null); in that case the
 * card shows a small "尚未翻译" tag in the meta row.
 */

import { el } from "../dom";
import type { DailyListItem } from "../types";
import { navigate } from "../router";

export interface ArticleCardOptions {
  item: DailyListItem;
  /** Sequential number within the day group (1-based). */
  ordinal: number;
}

export function articleCard({ item, ordinal }: ArticleCardOptions): HTMLElement {
  const abstractSection = renderAbstract(item);
  const openUrl = pubmedUrl(item.pmid);

  return el("article", { class: "article-card", id: `pmid-${item.pmid}` }, [
    el("span", { class: "article-card-ordinal" }, `${ordinal}.`),
    el(
      "div",
      { class: "article-card-body" },
      [
        // Title — both EN and ZH rendered; CSS toggle decides which side
        // wins. English is on top in the layout flow so it appears first
        // when language=EN, Chinese below; when language=ZH the CSS
        // hides EN, leaving ZH alone.
        el("h3", { class: "article-card-title-block" }, [
          el("a", {
            class: "article-card-title article-card-title-en",
            href: `#/article/${item.pmid}`,
            onclick: (ev: Event) => {
              // Programmatic navigation so the sidebar / hover styles
              // still work — `href` is also wired for right-click / cmd-click.
              const me = ev as MouseEvent;
              if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
              ev.preventDefault();
              navigate(`/article/${item.pmid}`);
            },
            title: item.title,
          }, item.title),
          el("a", {
            class: "article-card-title article-card-title-zh",
            href: `#/article/${item.pmid}`,
            onclick: (ev: Event) => {
              const me = ev as MouseEvent;
              if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
              ev.preventDefault();
              navigate(`/article/${item.pmid}`);
            },
          }, item.title_zh ?? "（暂无中文翻译）"),
        ]),
        renderMeta(item, openUrl),
        abstractSection.root,
      ],
    ),
  ]);
}

// ─── abstract (expand / collapse) ─────────────────────────────────────

function renderAbstract(item: DailyListItem): { root: HTMLElement; toggle: () => void } {
  const hasZh = Boolean(item.abstract_zh);
  const enText = item.abstract?.trim() ?? "";
  const zhText = item.abstract_zh?.trim() ?? "";

  const root = el("div", { class: "article-card-abstract" });
  if (!enText && !zhText) {
    root.appendChild(el("p", { class: "muted" }, "（无摘要）"));
    return { root, toggle: () => {} };
  }

  let expanded = false;
  const en = el(
    "p",
    { class: "article-card-abstract-en clamp" },
    enText || "（无英文摘要）",
  );
  const zh = el(
    "p",
    { class: "article-card-abstract-zh clamp" },
    zhText || "（无中文摘要）",
  );
  const enClamp = en.classList.contains("clamp");
  const zhClamp = zh.classList.contains("clamp");

  const toggle = el(
    "button",
    {
      class: "abstract-toggle",
      type: "button",
      onclick: () => {
        expanded = !expanded;
        if (enClamp) en.classList.toggle("clamp", !expanded);
        if (zhClamp) zh.classList.toggle("clamp", !expanded);
        toggle.textContent = expanded ? "收起" : "展开";
      },
    },
    "展开",
  );

  if (enText) root.appendChild(en);
  if (hasZh) {
    root.appendChild(zh);
  }
  root.appendChild(toggle);

  return {
    root,
    toggle: () => {
      const btn = root.querySelector(".abstract-toggle") as HTMLButtonElement | null;
      btn?.click();
    },
  };
}

// ─── meta row ─────────────────────────────────────────────────────────

function renderMeta(item: DailyListItem, pubmedHref: string | null): HTMLElement {
  const parts: HTMLElement[] = [];

  const journalAbbr = item.journal_abbr ?? item.journal;
  if (journalAbbr) {
    parts.push(
      el("span", { class: "meta-journal" }, journalAbbr),
    );
  }
  if (item.pmid) {
    parts.push(
      el("span", { class: "meta-separator" }, "·"),
    );
    parts.push(
      el(
        "a",
        {
          class: "meta-link",
          href: pubmedHref ?? "#",
          target: "_blank",
          rel: "noopener noreferrer",
        },
        `PMID ${item.pmid}`,
      ),
    );
  }

  // Journal-metric badges — only render if the daily item has them set.
  const jcr = item.jcr_quartile;
  if (jcr) {
    const q = jcr.toUpperCase();
    parts.push(
      el("span", { class: `meta-badge is-jcr-${q.toLowerCase()}` }, jcr),
    );
  }
  if (item.impact_factor !== null && item.impact_factor !== undefined) {
    parts.push(
      el("span", { class: "meta-badge" }, `IF ${formatIf(item.impact_factor)}`),
    );
  }
  if (item.new_talent_quartile) {
    parts.push(
      el("span", { class: "meta-badge" }, `新锐 ${item.new_talent_quartile}`),
    );
  }
  if (!item.abstract_zh && item.abstract) {
    parts.push(
      el("span", { class: "meta-badge" }, "尚未翻译"),
    );
  }

  return el("div", { class: "article-card-meta" }, parts);
}

function formatIf(value: number): string {
  if (value >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function pubmedUrl(pmid: string): string {
  return `https://pubmed.ncbi.nlm.nih.gov/${encodeURIComponent(pmid)}/`;
}
